<template>
  <div>
    <app-button
      class="mr-4"
      is-icon-color-custom
      @click="dialogIsOpen = true"
    >
      <template #left-icon>
        <app-material-icon name="add" color="icon-inverted" />
      </template>
      {{ $t('subscriptions.AddSubscriberDialog.newUser') }}
    </app-button>
    <app-modal v-model="dialogIsOpen"
               size="lg"
               :ok-disabled="postNewSubscriberPending"
               :ok-loading="postNewSubscriberPending"
               :cancel-disabled="postNewSubscriberPending"
               @ok="preparePostSSubscriber"
               @cancel="resetAndClose"
    >
      <template #title>{{ $t('subscriptions.AddSubscriberDialog.title') }}</template>
      <template #body>
        <div v-if="seatsPending && !confirmationDialogOpen" class="text-center">
          <v-progress-circular indeterminate size="48" />
        </div>
        <template v-else>
          <v-row>
            <v-col>
              <i18n-t keypath="common.requiredFieldsInfo" tag="p" class="text-caption text-text-tertiary" scope="global">
                <template #asterisk>
                  <span class="text-text-danger">*</span>
                </template>
              </i18n-t>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <form novalidate @submit.stop.prevent="preparePostSSubscriber"
              >
                <v-row>
                  <v-col>
                    <AppTextField v-model.trim="newSubscriber.firstName"
                                  :label="$t('common.form.firstName')"
                                  class="required"
                                  :error-messages="newSubscriberFirstNameErrors"
                                  maxlength="50"
                                  @blur="v$.newSubscriber.firstName.$touch"
                    ></AppTextField>
                  </v-col>
                  <v-col>
                    <AppTextField v-model.trim="newSubscriber.lastName"
                                  :label="$t('common.form.lastName')"
                                  class="required"
                                  :error-messages="newSubscriberLastNameErrors"
                                  maxlength="50"
                                  @blur="v$.newSubscriber.lastName.$touch"
                    ></AppTextField>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <AppTextField v-model.trim="newSubscriber.company"
                                  :label="$t('common.form.company')"
                                  maxlength="100"
                    ></AppTextField>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <AppTextField v-model.trim="newSubscriber.email"
                                  :label="$t('common.form.email')"
                                  class="required"
                                  type="email"
                                  :error-messages="newSubscriberEmailErrors"
                                  maxlength="255"
                                  @blur="v$.newSubscriber.email.$touch"
                    ></AppTextField>
                  </v-col>
                  <v-col>
                    <AppSelect v-model="newSubscriber.locale"
                               :error-messages="newSubscriberLocaleErrors"
                               :items="localeOptions"
                               :label="$t('common.form.locale')"
                               class="required"
                               item-title="text"
                               item-value="value"
                    >
                      <template #selection="{ item }">
                        <div class="d-flex align-center">
                          <v-img :src="item.raw.icon" :width="25" class="mr-2" />
                          <span>{{ item.raw.text }}</span>
                        </div>
                      </template>

                      <template #item="{ item, props }">
                        <v-list-item v-bind="props">
                          <template #title>
                            <div class="d-flex align-center">
                              <v-img :src="item.raw.icon"
                                     :width="25"
                                     class="mr-2"
                                     inline
                              />
                              <span>{{ item.raw.text }}</span>
                            </div>
                          </template>
                        </v-list-item>
                      </template>
                    </AppSelect>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-checkbox v-model="newSubscriber.isUser"
                                class="mt-0 pt-0"
                                :loading="seatsPending"
                                density="comfortable"
                                hide-details
                    >
                      <template #label>
                        <div class="d-flex align-center">
                          <span>{{$t('subscriptions.AddSubscriberDialog.subscriptionUser')}}</span>
                          <AppHelpTooltip
                            :text="$t('subscriptions.AddSubscriberDialog.subscriptionUserTooltip', {name: subscription.name})"
                          >
                          </AppHelpTooltip>
                          <span v-if="nextPrice" class="">{{$t('subscriptions.AddSubscriberDialog.licensePriceAbbr', { price: nextPrice })}}</span>
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox v-model="newSubscriber.isAdmin"
                                class="pt-0"
                                density="comfortable"
                                hide-details
                    >
                      <template #label>
                        <div class="d-flex align-center">
                          <span>{{$t('subscriptions.AddSubscriberDialog.subscriptionAdmin')}}</span>
                          <AppHelpTooltip :text="$t('subscriptions.AddSubscriberDialog.subscriptionAdminTooltip')"></AppHelpTooltip>
                        </div>
                      </template>
                    </v-checkbox>
                    <v-checkbox v-model="newSubscriber.isBillingAdmin"
                                class="pt-0"
                                density="comfortable"
                                hide-details
                    >
                      <template #label>
                        <div class="d-flex align-center">
                          <span>{{$t('subscriptions.AddSubscriberDialog.subscriptionBillingAdmin')}}</span>
                          <AppHelpTooltip :text="$t('subscriptions.AddSubscriberDialog.subscriptionBillingAdminTooltip')"></AppHelpTooltip>
                        </div>
                      </template>
                    </v-checkbox>
                    <div v-if="roleError" class="mt-2 ml-3 text-error">{{$t('subscriptions.AddSubscriberDialog.roleError')}}</div>
                  </v-col>
                </v-row>
              </form>
            </v-col>
          </v-row>
        </template>
      </template>
    </app-modal>
    <app-modal v-model="confirmationDialogOpen"
               size="xs"
               :ok-disabled="seatsPending || seatsError"
               :cancel-disabled="seatsPending"
               @ok="confirmPostSubscriber"
               @cancel="confirmationDialogOpen = false"
    >
      <template #title>{{ $t('subscriptions.AddSubscriberDialog.confirmationDialogTitle') }}</template>
      <template #body>
        <div v-if="seatsPending" class="text-center">
          <v-progress-circular indeterminate size="48" />
        </div>
        <p v-else>{{confirmationDialogBody}}</p>
      </template>
    </app-modal>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapState } from 'vuex'

import AppHelpTooltip from '@/common/AppHelpTooltip.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import { countryFlags } from '@/common/assets'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { POST_NEW_SUBSCRIBER, GET_SEATS } from '@/store/modules/subscription/action_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

export default {
  name: 'AddSubscriberDialog',
  components: { AppHelpTooltip, AppTextField, AppSelect },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    subscription: {
      type: Object,
      required: true,
    },
  },
  validations: {
    newSubscriber: {
      firstName: { required },
      lastName: { required },
      email: { required, postmarkEmail },
      locale: { required },
    },
  },
  data () {
    return {
      dialogIsOpen: false,
      confirmationDialogOpen: false,
      nextPrice: 0,
      licenseIncluded: false,
      skipCheckSeats: false,
      newSubscriber: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        locale: null,
        isAdmin: false,
        isBillingAdmin: false,
        isUser: true,
        confirmMonthly: false,
        confirmYearly: false,
      },
      localeOptions: [
        {
          text: this.$t('subscriptions.AddSubscriberDialog.locale.french'),
          value: 'fr',
          icon: countryFlags.fr,
        },
        {
          text: this.$t('subscriptions.AddSubscriberDialog.locale.english'),
          value: 'en',
          icon: countryFlags.en,
        },
        {
          text: this.$t('subscriptions.AddSubscriberDialog.locale.german'),
          value: 'de',
          icon: countryFlags.de,
        },
      ],
      roleError: false,
    }
  },
  computed: {
    ...mapState('subscription', ['postNewSubscriberPending', 'seatsPending', 'seats', 'seatsError']),
    newSubscriberFirstNameErrors () {
      return formFieldErrors(
        this.v$.newSubscriber.firstName,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.firstName') })],
        ],
      )
    },
    newSubscriberLastNameErrors () {
      return formFieldErrors(
        this.v$.newSubscriber.lastName,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.lastName') })],
        ],
      )
    },
    newSubscriberEmailErrors () {
      return formFieldErrors(
        this.v$.newSubscriber.email,
        [
          ['postmarkEmail', this.$t('common.validations.email')],
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.email') })],
        ],
      )
    },
    newSubscriberLocaleErrors () {
      return formFieldErrors(
        this.v$.newSubscriber.locale,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.locale') })],
        ],
      )
    },
    hasOneRoleChecked () {
      return this.newSubscriber.isAdmin || this.newSubscriber.isUser || this.newSubscriber.isBillingAdmin
    },
    confirmationDialogBody () {
      if (this.skipCheckSeats || this.licenseIncluded) {
        return this.$t('subscriptions.AddSubscriberDialog.licenseIncluded')
      } else if (this.nextPrice) {
        return this.$t('subscriptions.AddSubscriberDialog.licensePrice', { price: this.nextPrice })
      } else {
        return this.$t('subscriptions.AddSubscriberDialog.getSeatsError')
      }
    },
  },
  watch: {
    async dialogIsOpen (val) {
      if (val === true) {
        await this.GET_SEATS()
        this.initSeatsData(this.seats)
        this.newSubscriber.company = this.subscription.company
      }
    },
    hasOneRoleChecked (val) {
      if (val) {
        this.roleError = false
      }
    },
  },
  methods: {
    ...mapActions('subscription', [POST_NEW_SUBSCRIBER, GET_SEATS]),
    resetAndClose () {
      this.dialogIsOpen = false
      this.nextPrice = 0
      this.licenseIncluded = false
      this.confirmationDialogOpen = false
      this.newSubscriber = {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        locale: null,
        isAdmin: false,
        isBillingAdmin: false,
        isUser: true,
        confirmMonthly: false,
        confirmYearly: false,
      }
      this.v$.$reset()
    },
    initSeatsData (seats) {
      if (seats.billingSeatsAvailable <= 0 && (seats.nextYearlyPrice > 0 || seats.nextMonthlyPrice > 0)) {
        if (seats.nextYearlyPrice) {
          this.nextPrice = seats.nextYearlyPrice
          this.newSubscriber.confirmYearly = true
          this.newSubscriber.confirmMonthly = false
        } else {
          this.nextPrice = seats.nextMonthlyPrice
          this.newSubscriber.confirmMonthly = true
          this.newSubscriber.confirmYearly = false
        }
      } else {
        this.nextPrice = 0
        this.licenseIncluded = true
      }
    },
    preparePostSSubscriber () {
      this.v$.$touch()
      this.roleError = false
      if (!this.hasOneRoleChecked) {
        this.roleError = true
      } else if (!this.v$.$invalid) {
        if (!this.newSubscriber.isUser) {
          this.skipCheckSeats = true
        } else {
          this.skipCheckSeats = false
          this.GET_SEATS().then(() => this.initSeatsData(this.seats))
        }
        this.confirmationDialogOpen = true
      }
    },
    async confirmPostSubscriber () {
      this.confirmationDialogOpen = false
      try {
        await this.POST_NEW_SUBSCRIBER(this.newSubscriber)
        toast.success(this.$t('subscriptions.AddSubscriberDialog.postSubscriberSuccess'))
        this.$emit('postNewSubscriberSuccess')
        this.resetAndClose()
      } catch (error) {
        toast.error(this.$t('subscriptions.AddSubscriberDialog.postSubscriberError'))
      }
    },
  },
}
</script>
